import React, { useState, useEffect } from 'react';
import api from '../services/api';
import BudgetSettingsModal from './BudgetSettingsModal';
import './AIAnalysis.css';

function AIAnalysis({ income, expenses, selectedMonth, selectedYear, months, onSettingsChange }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [budgetSettings, setBudgetSettings] = useState({
    needs_percentage: 60,
    wants_percentage: 30,
    savings_percentage: 10
  });
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    fetchBudgetSettings();
  }, []);

  const fetchBudgetSettings = async () => {
    try {
      const response = await api.get('/settings/budget');
      setBudgetSettings(response.data);
    } catch (error) {
      console.error('Error fetching budget settings:', error);
      // Use defaults if fetch fails
    }
  };

  const parseMarkdown = (text) => {
    if (!text) return '';
    
    // Convert markdown to HTML
    let html = text;
    
    // Headers (## Header)
    html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
    
    // Bold (**text**)
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    
    // Bullet points (• or - or *)
    html = html.replace(/^[•\-\*] (.+)$/gm, '<li>$1</li>');
    
    // Wrap consecutive <li> in <ul>
    html = html.replace(/(<li>.*?<\/li>\n?)+/g, (match) => `<ul>${match}</ul>`);
    
    // Paragraphs (lines that aren't headers or list items)
    html = html.split('\n').map(line => {
      line = line.trim();
      if (!line) return '';
      if (line.startsWith('<h2>') || line.startsWith('<ul>') || line.startsWith('</ul>') || line.startsWith('<li>')) {
        return line;
      }
      return `<p>${line}</p>`;
    }).join('\n');
    
    return html;
  };

  const generateAnalysis = async () => {
    try {
      setLoading(true);
      setError(null);

      // Prepare data summary
      const totalIncome = income.reduce((sum, item) => sum + parseFloat(item.net_amount || 0), 0);
      const totalExpenses = expenses.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
      const netSavings = totalIncome - totalExpenses;
      const savingsRate = totalIncome > 0 ? ((netSavings / totalIncome) * 100) : 0;

      // Category breakdown
      const categoryTotals = {};
      expenses.forEach(expense => {
        const category = expense.category;
        if (!categoryTotals[category]) {
          categoryTotals[category] = 0;
        }
        categoryTotals[category] += parseFloat(expense.amount || 0);
      });

      const topCategories = Object.entries(categoryTotals)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([category, amount]) => `${category}: €${amount.toFixed(2)}`);

      const periodLabel = selectedMonth === 'all' 
        ? `full year ${selectedYear}`
        : `${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}`;

      // Get current date context
      const today = new Date();
      const currentDay = today.getDate();
      const currentMonth = today.getMonth() + 1; // 1-12
      const currentYear = today.getFullYear();
      const currentMonthName = today.toLocaleString('en-US', { month: 'long' });
      
      // Calculate how much of the period has elapsed
      let periodElapsedContext = '';
      if (selectedMonth === 'all') {
        // Yearly view
        if (parseInt(selectedYear) === currentYear) {
          const daysInYear = 365;
          const dayOfYear = Math.floor((today - new Date(currentYear, 0, 0)) / (1000 * 60 * 60 * 24));
          const percentElapsed = ((dayOfYear / daysInYear) * 100).toFixed(1);
          periodElapsedContext = `\n\n**CRITICAL DATE CONTEXT:** Today is ${currentMonthName} ${currentDay}, ${currentYear}. You are analyzing data for ${selectedYear}, but ONLY ${percentElapsed}% of the year has passed (${dayOfYear} days). This means the user has only had ~${currentMonth - 1} full months plus ${currentDay} days of the current month to accumulate transactions. DO NOT flag "missing expenses" or "incomplete data" - the year is simply not finished yet. Extrapolate annual projections from the available data if needed.`;
        } else if (parseInt(selectedYear) > currentYear) {
          periodElapsedContext = `\n\n**CRITICAL DATE CONTEXT:** Today is ${currentMonthName} ${currentDay}, ${currentYear}. You are analyzing data for ${selectedYear}, which is IN THE FUTURE. Any data present may be scheduled/planned transactions. DO NOT expect complete data.`;
        } else {
          periodElapsedContext = `\n\n**DATE CONTEXT:** Today is ${currentMonthName} ${currentDay}, ${currentYear}. You are analyzing historical data for ${selectedYear}, which is a complete past year.`;
        }
      } else {
        // Monthly view
        const selectedMonthNum = parseInt(selectedMonth);
        if (parseInt(selectedYear) === currentYear && selectedMonthNum === currentMonth) {
          const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
          const percentElapsed = ((currentDay / daysInMonth) * 100).toFixed(1);
          periodElapsedContext = `\n\n**CRITICAL DATE CONTEXT:** Today is ${currentMonthName} ${currentDay}, ${currentYear}. You are analyzing the CURRENT month, but only ${percentElapsed}% has passed (${currentDay} of ${daysInMonth} days). DO NOT flag incomplete data - the month is still in progress.`;
        } else if (parseInt(selectedYear) > currentYear || (parseInt(selectedYear) === currentYear && selectedMonthNum > currentMonth)) {
          periodElapsedContext = `\n\n**CRITICAL DATE CONTEXT:** Today is ${currentMonthName} ${currentDay}, ${currentYear}. You are analyzing a FUTURE month. Any data present may be scheduled/planned transactions.`;
        }
      }

      const periodContext = selectedMonth === 'all'
        ? `This is a YEARLY analysis for ${selectedYear}. Focus on annual patterns, long-term trends, and overall financial health for the entire year.${periodElapsedContext}`
        : `This is a MONTHLY analysis for ${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}. Focus on this specific month's performance, comparing it to typical monthly expectations.${periodElapsedContext}`;

      const prompt = `You are a personal financial advisor analyzing someone's finances for ${periodLabel}. 

**IMPORTANT CONTEXT:** ${periodContext}

Financial Summary:
- Total Income: €${totalIncome.toFixed(2)}
- Total Expenses: €${totalExpenses.toFixed(2)}
- Net Savings: €${netSavings.toFixed(2)}
- Savings Rate: ${savingsRate.toFixed(1)}%
- Number of Income Transactions: ${income.length}
- Number of Expense Transactions: ${expenses.length}

Top Spending Categories:
${topCategories.join('\n')}

Please provide a comprehensive analysis using the user's CUSTOM budget targets:

**User's Budget Targets:**
- ${budgetSettings.needs_percentage}% for NEEDS (essentials: housing, utilities, groceries, transportation, insurance, minimum debt payments)
- ${budgetSettings.wants_percentage}% for WANTS (non-essentials: dining out, entertainment, hobbies, shopping, subscriptions)
- ${budgetSettings.savings_percentage}% for SAVINGS & INVESTMENTS (emergency fund, retirement, investments, extra debt payments)

**IMPORTANT:** The user has set custom targets of ${budgetSettings.needs_percentage}/${budgetSettings.wants_percentage}/${budgetSettings.savings_percentage} instead of the standard 60/30/10. Use THESE specific percentages in your analysis.

**Your analysis should include:**

1. **Overall Financial Health** (2-3 sentences)
   - Brief assessment of their current situation
   - How they're doing overall

2. **${budgetSettings.needs_percentage}/${budgetSettings.wants_percentage}/${budgetSettings.savings_percentage} Target Analysis**
   - Estimate their current breakdown based on the categories shown
   - Compare to their CUSTOM ${budgetSettings.needs_percentage}/${budgetSettings.wants_percentage}/${budgetSettings.savings_percentage} split
   - Identify any concerning imbalances

3. **Key Insights** (3-4 specific points)
   - Notable patterns in their spending
   - Red flags or positive behaviors
   - Missing expense categories that might not be tracked

4. **Actionable Recommendations** (3 specific actions)
   - Practical steps to improve their finances
   - Focus on realistic, achievable changes
   - Prioritize based on impact

Format your response with clear headers using ## for sections and bullet points (•) for lists. Keep it concise, friendly, and actionable. Use euros (€) for all amounts.`;

      const response = await api.post('/ai/analyze', {
        prompt: prompt,
        context: {
          totalIncome,
          totalExpenses,
          netSavings,
          savingsRate,
          period: periodLabel,
          isYearly: selectedMonth === 'all'
        }
      });

      setAnalysis(response.data.analysis);
    } catch (error) {
      console.error('Error generating analysis:', error);
      setError('Failed to generate analysis. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ai-analysis-card">
      <div className="ai-analysis-header" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
        <span className="ai-icon" style={{ fontSize: '16px', flexShrink: 0 }}>🤖</span>
        <h3 style={{ fontSize: '12px', margin: 0, whiteSpace: 'nowrap' }}>AI Financial Analysis</h3>
        <span className="budget-target-badge" style={{ padding: '2px 6px', fontSize: '9px', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {budgetSettings.needs_percentage}/{budgetSettings.wants_percentage}/{budgetSettings.savings_percentage}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', flexShrink: 0 }}>
          <button 
            className="settings-btn"
            onClick={() => setShowSettings(true)}
            title="Customize budget targets"
            style={{ width: '28px', height: '28px', padding: 0, fontSize: '12px' }}
          >
            ⚙️
          </button>
          <button 
            className="analyze-btn"
            onClick={generateAnalysis}
            disabled={loading || income.length === 0}
            style={{ padding: '6px 10px', fontSize: '11px', whiteSpace: 'nowrap' }}
          >
            {loading ? 'Analyzing...' : 'Generate'}
          </button>
        </div>
      </div>

      {error && (
        <div className="ai-error">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {!analysis && !loading && !error && (
        <div className="ai-empty">
          <div className="empty-icon">💡</div>
          <p>Click "Generate Analysis" to get AI-powered insights about your finances</p>
        </div>
      )}

      {analysis && (
        <div className="ai-content">
          <div className="analysis-text" dangerouslySetInnerHTML={{ __html: parseMarkdown(analysis) }} />
          <div className="analysis-footer">
            <span className="disclaimer">
              ℹ️ This is AI-generated guidance for informational purposes only, not financial advice.
            </span>
          </div>
        </div>
      )}

      {showSettings && (
        <BudgetSettingsModal
          onClose={() => setShowSettings(false)}
          onSave={(newSettings) => {
            setBudgetSettings(newSettings);
            setAnalysis(null); // Clear analysis so user regenerates with new settings
            if (onSettingsChange) {
              onSettingsChange(); // Notify parent that settings changed
            }
          }}
        />
      )}
    </div>
  );
}

export default AIAnalysis;
